import React from 'react';
import ModalImage from 'react-modal-image';
import _ from 'underscore';

import DropDown from '../../../../commonComponents/DropDown/DropDown';
import ImgIcon from '../../../../commonComponents/Images/default-img.png';
import { SimpleInput } from '../../../../commonComponents/Input/Input';
import ChapterDefinitions from '../../../../commonComponents/Modals/ChapterDefinitions/ChapterDefinitions';
import EditQuestionModal from '../../../../commonComponents/Modals/EditQuestionModal/EditQuestionModal';
import { spellCheckContent } from '../../../../lib/spellCheckContent';
import ConvertHtmlToPlainText from '../../../../utilities/ConvertHtmlToPlainText';
import nonModifiedQuestionOrAnswer from '../../../../utilities/nonModifiedQuestionOrAnswer';
import {
  endsWithQuestionMark,
  getDuplicateAnswers,
  hasQuoteCharacter,
} from '../../../../utilities/questionValidation';
import { sanitizeRichText } from '../../../../utilities/reRenderFormulas';
import UpdateQuestionIndexModal from './Modals/UpdateQuestionIndexModal/UpdateQuestionIndexModal';

// One tooltip instance per kind is rendered by the grid and shared by every
// row, so a long chapter does not mount one per question.
export const VALIDATION_ISSUES_TOOLTIP_ID = 'validationIssuesTooltip';
export const QUESTION_ID_TOOLTIP_ID = 'questionIdTooltip';

export default class QuestionGridRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isQuestionIndexModalOpen: false,
      questionsCheckDeleted: [],
    };
  }

  spellCheck(content) {
    if (this.props.spellCheckCells) {
      return spellCheckContent(content);
    }
    return content;
  }

  componentDidMount() {
    this.props.setSeletedQuestionsForDeleted('', false);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.questionsForDeleted !== prevProps.questionsForDeleted) {
      this.setState({
        questionsCheckDeleted: this.props.questionsForDeleted,
      });
    }
    if (
      this.props.isShowDeletedQuestions !== prevProps.isShowDeletedQuestions
    ) {
      this.props.setSeletedQuestionsForDeleted('', false);
    }

    if (
      this.props.question !== prevProps.question &&
      this.props.modalVisibility
    ) {
      this.openEditionModal(
        this.props.question,
        this.props.question,
        'Question',
        false,
        true,
        this.props.question.multipleOptionMode,
      );
    }
  }

  handleToggleQuestionIndexModal = () => {
    this.setState({
      isQuestionIndexModalOpen: !this.state.isQuestionIndexModalOpen,
    });
  };

  handleChangeQuestionIndex = (question, newIndex) => {
    const questionUpdated = { ...question };
    questionUpdated.presentationIndex = newIndex;
    this.props.onEditPresentationIndex(
      questionUpdated,
      this.props.assignment ? this.props.assignment.id : null,
    );
  };

  getMoveUp = (question) => {
    if (question.presentationIndex === 1) return null;
    const toMove = this.props.questions[question.presentationIndex - 1];
    if (!toMove) return null;
    return {
      name: 'Move up',
      event: () => {
        const questionUpdated = { ...question };
        questionUpdated.presentationIndex = question.presentationIndex - 1;
        this.props.onEditPresentationIndex(
          questionUpdated,
          this.props.assignment ? this.props.assignment.id : null,
        );
      },
      icon: <i className="fa fa-arrow-circle-up fa-lg" />,
      style: { display: 'flex', alignItems: 'center' },
    };
  };

  getMoveDown = (question) => {
    const toMove = this.props.questions[question.presentationIndex];
    if (!toMove) return null;
    return {
      name: 'Move down',
      event: () => {
        const questionUpdated = { ...question };
        questionUpdated.presentationIndex = question.presentationIndex + 1;
        this.props.onEditPresentationIndex(
          questionUpdated,
          this.props.assignment ? this.props.assignment.id : null,
        );
      },
      icon: <i className="fa fa-arrow-circle-down fa-lg" />,
      style: { display: 'flex', alignItems: 'center' },
    };
  };

  getOptions = (question) => {
    if (!this.props.editPermission) {
      return [];
    }
    const moveUp = this.getMoveUp(question);
    const moveDown = this.getMoveDown(question);
    const options = [
      {
        name: 'Move to Index',
        event: () => {
          this.setState({ isQuestionIndexModalOpen: true });
        },
        icon: <i className="fa fa-sort fa-lg" style={{ marginLeft: '13px' }} />,
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
        },
      },
      {
        name: question.multipleOptionMode
          ? 'Switch to Flaschard mode'
          : 'Switch to Multiple Option',
        event: () => this.changeMode(),
        icon: <i className="fa fa-exchange fa-lg" />,
        style: { display: 'flex', alignItems: 'center' },
      },
      {
        name: 'Edit definitions',
        event: () => this.openQuestionsModal(question),
        icon: <i className="fa fa-pencil fa-lg" />,
        style: { display: 'flex', alignItems: 'center' },
      },
    ];

    if (this.props.assignment && this.props.assignment.status === 'Submitted')
      options.push({
        name: 'View changes',
        event: () => this.props.openHistoryView(question),
        icon: <i className="fa fa-history fa-lg mt-2" />,
        style: { display: 'flex', alignItems: 'center' },
      });
    if (moveDown) options.unshift(moveDown);
    if (moveUp) options.unshift(moveUp);

    return options;
  };

  changeMode = () => {
    const questionUpdated = this.props.question;
    questionUpdated.multipleOptionMode =
      !this.props.question.multipleOptionMode;
    questionUpdated.flashMode = !this.props.question.flashMode;
    this.props.onEdit(
      questionUpdated,
      this.props.assignment ? this.props.assignment.id : null,
    );
  };

  openQuestionsModal = (question) => {
    this.props.openModal({
      modalClassName: 'modal-question-definitions',
      modalContent: (
        <ChapterDefinitions
          reference={
            this.props.assignment
              ? this.props.assignment.chapter.reference
              : this.props.reference
          }
          question={question}
          closeModal={this.props.closeModal}
          assignment={this.props.assignment}
        />
      ),
    });
  };

  renderAnswer = (
    originalResponse,
    answer,
    width,
    question,
    labelForEditionModal,
    index,
    duplicateAnswers,
  ) => {
    if (!answer) {
      return '';
    }
    const hasOrder = answer.order !== 0 && answer.order !== null;
    const aiAnswer =
      originalResponse && originalResponse.ai_answer && answer.correct;
    const hasContentError =
      hasQuoteCharacter(answer.text) || duplicateAnswers?.has(answer);

    return (
      <div
        style={{ width, cursor: 'pointer' }}
        className={`${hasOrder ? 'tRowWithOrder' : 'tRow'}${
          hasContentError ? ' cellContentError' : ''
        }`}
        key={index}
        onClick={() =>
          this.openEditionModal(answer, question, labelForEditionModal, true)
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          {question.multipleOptionMode && hasOrder && (
            <div className="bold orderAnswer">Order: {answer.order}</div>
          )}
          <div className={aiAnswer ? 'columnDirection' : null}>
            <div
              style={{ height: '100%' }}
              className="questionNonModified leftJustify"
            >
              <div className="questionGridCellContent leftJustify">
                <div
                  className="textContainer"
                  title={answer.text}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichText(this.renderText(answer.text)),
                  }}
                />
                {answer.imageUrl?.includes('http') && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="image-question-preview-container"
                  >
                    <ModalImage
                      small={`${answer.imageUrl}?t=${Date.now()}`}
                      large={`${answer.imageUrl}?t=${Date.now()}`}
                      className="image-question-preview"
                      hideDownload
                      hideZoom
                    />
                  </div>
                )}
              </div>
            </div>
            {originalResponse &&
              originalResponse.ai_answer &&
              answer.correct && (
                <div style={{ height: '100%' }}>
                  <div
                    className={`titleOriginalResponseContainer ${answer.substantialChanges && 'titleOriginalResponseSubstantialChanges'}`}
                  >
                    <span className="title">Original AI Answer</span>
                  </div>

                  <div
                    className={`originalResponseContainerGrid ${answer.substantialChanges && 'originalResponseSubstantialChanges'}`}
                  >
                    <div
                      className="text"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(originalResponse.ai_answer),
                      }}
                    />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  };

  editQuestion = (newQuestion) => {
    if (
      typeof newQuestion.customPromptId === 'string' &&
      newQuestion.customPromptId !== ''
    ) {
      newQuestion.customPromptId = this.props.userCustomPrompts[0].id;
    }

    this.props.onEdit(
      newQuestion,
      this.props.assignment ? this.props.assignment.id : null,
    );
  };

  openEditionModal = (
    item,
    question,
    label,
    isAnswer = false,
    isQuestion = false,
    multipleOption = false,
    isPresentationIndex = false,
    isEditingPage = false,
    isFurtherReading = label === 'Further reading',
    isAIAdminFeedback = label === 'Admin AI Feedback',
  ) => {
    if (
      this.isQuestionDeleted(question) ||
      this.isQuestionDisabled(question) ||
      !this.props.editPermission
    ) {
      return;
    }
    this.props.openModal({
      modalClassName: 'modal-edit-question',
      modalContent: (
        <EditQuestionModal
          question={question}
          closeModal={this.props.closeModal}
          item={item}
          title={label}
          isAnswer={isAnswer}
          textarea={isAnswer || isQuestion || isAIAdminFeedback}
          onSubmit={this.editQuestion}
          isQuestion={isQuestion}
          isPresentationIndex={isPresentationIndex}
          assignment={this.props.assignment ? this.props.assignment.id : null}
          multipleOption={multipleOption}
          isEditingPage={isEditingPage}
          setIsCreatingAiQuestion={this.props.setIsCreatingAiQuestion}
          isCreatingAiQuestion={this.props.isCreatingAiQuestion}
          userCustomPrompts={this.props.userCustomPrompts}
          createUserCustomPrompt={this.props.createUserCustomPrompt}
          chapterId={this.props.chapter.id}
          isFurtherReading={isFurtherReading}
          isAIAdminFeedback={isAIAdminFeedback}
          improveQuestionWithAi={this.props.improveQuestionWithAi}
          showWarning={this.props.showWarning}
          isSaveQuestion={this.props.isSaveQuestion}
          updateIncludeNotesInSuggestion={
            this.props.updateIncludeNotesInSuggestion
          }
          permissions={this.props.permissions}
          permissionPrefix={this.props.permissionPrefix}
          aiFeedbackTemplates={this.props.aiFeedbackTemplates}
          spellCheckEnabled={this.props.spellCheckCells}
        />
      ),
    });
  };

  getOptionsLabel = (index) => {
    switch (index) {
      case 0:
        return 'Option A';

      case 1:
        return 'Option B';

      case 2:
        return 'Option C';

      default:
        return 'Option D';
    }
  };

  renderImgIcon = (notes) => {
    const hasImg = notes ? notes.includes('img') : null;
    return hasImg
      ? `<img src=${ImgIcon} alt="Has imgs" class="default-img" />`
      : null;
  };

  buildBlueText = (definitions, originalText, htmlText) => {
    let newText = originalText;
    if (!newText) return newText;
    definitions.map((definition) => {
      const position = newText.search(definition.term);
      if (position !== -1) {
        newText = `${newText.substring(
          0,
          position,
        )}<span class="blueText" title="${definition.definition}">${
          definition.term
        }</span>${newText.substring(
          position + definition.term.length,
          newText.length,
        )}`;
      }
      return definition;
    });
    let img;
    if (htmlText) {
      img = this.renderImgIcon(htmlText);
    }
    if (img && htmlText && !this.renderImgIcon(newText))
      newText = `<div class="textWrapper">${newText}</div>${img}`;
    return newText;
  };

  renderText = (text, textHtml = null) => {
    let questionDefinitions;
    if (typeof this.props.question.definitions === 'object') {
      questionDefinitions = this.props.question.definitions ?? [];
    } else {
      questionDefinitions = this.props.question.definitions
        ? JSON.parse(this.props.question.definitions)
        : [];
    }
    const referenceDefinitions = this.props.reference.definitions ?? [];
    if (
      (!questionDefinitions || questionDefinitions.length === 0) &&
      (!referenceDefinitions || referenceDefinitions.length === 0)
    )
      return this.spellCheck(text);
    if (questionDefinitions?.length > 0 && referenceDefinitions.length > 0) {
      return this.spellCheck(
        this.buildBlueText(
          referenceDefinitions,
          this.buildBlueText(questionDefinitions, text, textHtml),
          textHtml,
        ),
      );
    }

    if (questionDefinitions?.length > 0 && referenceDefinitions.length === 0)
      return this.spellCheck(
        this.buildBlueText(questionDefinitions, text, textHtml),
      );
    if (questionDefinitions?.length === 0 && referenceDefinitions.length > 0)
      return this.spellCheck(
        this.buildBlueText(referenceDefinitions, text, textHtml),
      );
  };

  isQuestionDisabled = (question) => {
    let isInAssignment = false;
    let isInCurrentAssignment = false;
    if (this.props.assignment?.disabledQuestions) {
      isInAssignment = _.pluck(
        this.props.assignment.disabledQuestions,
        'id',
      ).includes(question.id);
    }
    if (!this.props.assignment && this.props.chapter?.assignments?.length) {
      isInCurrentAssignment = true;
    }
    return isInAssignment || isInCurrentAssignment;
  };

  isQuestionDeleted = (question) =>
    question && (question.status === 'Deleted' || question.deletedAt);

  answersForRender = (answers) => {
    const index = [2, 3];
    const forSend = answers;
    forSend.sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0));
    let counter = 1;
    let prevId;
    let hasCorrect = false;
    forSend.forEach((e) => {
      if (e.correct === true) hasCorrect = true;
    });
    if (!hasCorrect) forSend.push({ correct: true, order: 0, text: '', id: 1 });
    index.forEach((i) => {
      if (!forSend[i]) {
        prevId = forSend[i - 1] ? forSend[i - 1].id : 0;
        if (counter === 1) {
          forSend.push({ correct: false, order: 0, text: '', id: prevId + 2 });
        } else {
          forSend.push({ correct: false, order: 0, text: '', id: prevId + 3 });
        }
        counter++;
      }
    });
    return forSend;
  };

  getNonEmptyValue = (text, altText) => {
    if (text) {
      return text;
    }
    return altText;
  };

  renderWithoutImages = (furtherReading) => {
    let result = JSON.parse(JSON.stringify(furtherReading));
    if (furtherReading?.includes('<img')) {
      const start = furtherReading.indexOf('<img');
      const partial = furtherReading.slice(start);
      const end = partial.indexOf('>');
      result = furtherReading
        .slice(0, -(furtherReading.length - start))
        .concat(partial.slice(end + 1));
    }
    return result;
  };

  getFurtherReadingImage = (furtherReadingImage) => {
    let result = '';
    if (furtherReadingImage?.includes('<img')) {
      const start = furtherReadingImage.indexOf('<img') + 10;
      const partial = furtherReadingImage.slice(start);
      const end = partial.indexOf('"');
      result = partial.slice(0, -(partial.length - end));
    }
    return result;
  };

  handleCheckboxDeletedQuestion(question, checkQuestion) {
    let listQuestions = [...this.state.questionsCheckDeleted];
    if (!checkQuestion) {
      listQuestions.push(question);
    } else {
      listQuestions = listQuestions.filter((item) => item.id !== question.id);
    }

    this.setState({ questionsCheckDeleted: listQuestions });
    this.props.setSeletedQuestionsForDeleted(question, checkQuestion);
  }

  render() {
    const { question, isUpdating } = this.props;
    const multipleOption = question.multipleOptionMode;
    const isQuestionDisabled =
      this.isQuestionDeleted(question) || this.isQuestionDisabled(question);
    const isLineThrough = isQuestionDisabled ? ' LineThrough' : '';
    const qAnswers = this.answersForRender(question.answers).sort(
      (x, y) => y.correct - x.correct,
    );
    const correctAnswer = qAnswers[0];
    const nonModifiedQuestion = nonModifiedQuestionOrAnswer(true, question);

    const furtherReadingImage = this.getFurtherReadingImage(question.notes);
    const aiQuestion = question.originalResponse?.ai_question;
    const { questionsCheckDeleted } = this.state;
    const checkQuestion = questionsCheckDeleted.includes(question);

    const questionDisplayText = multipleOption
      ? this.getNonEmptyValue(question.text, question.flashQuestionText)
      : this.getNonEmptyValue(question.flashQuestionText, question.text);
    const questionCellErrorClass =
      hasQuoteCharacter(questionDisplayText) ||
      !endsWithQuestionMark(questionDisplayText)
        ? ' cellContentError'
        : '';
    const duplicateAnswers = getDuplicateAnswers(question.answers);
    // Only populated once Run validation has been pressed.
    const validationIssues =
      (this.props.validationIssues || {})[question.id] || [];

    return (
      <div
        className={`questionRowContainer${isLineThrough}`}
        style={{
          pointerEvents: isUpdating ? 'none' : 'auto',
        }}
      >
        {/* Column 1 - Index */}
        <div
          style={{
            width: '3%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
          className="tRow"
        >
          <div
            onClick={() =>
              this.openEditionModal(
                question,
                question,
                'Presentation Index',
                false,
                false,
                false,
                true,
              )
            }
            className="bold"
            style={{ cursor: 'pointer' }}
            data-tooltip-id={QUESTION_ID_TOOLTIP_ID}
            data-tooltip-content={`Question ID: ${question.id}`}
          >
            <div className="question-id">
              {/* The id used to replace the index on hover, which widened the
                  cell and pushed the issues icon out of the way: it is shown
                  in a tooltip instead, so nothing moves. */}
              <div className="presentation-index">
                {question.presentationIndex || 0}
              </div>
            </div>
          </div>
          {validationIssues.length > 0 && (
            <i
              className="fa fa-exclamation-triangle validationIssuesIcon"
              data-tooltip-id={VALIDATION_ISSUES_TOOLTIP_ID}
              data-tooltip-content={validationIssues.join('\n')}
            />
          )}
          <SimpleInput
            type="checkbox"
            notMb
            onChange={(e) => {
              this.handleCheckboxDeletedQuestion(question, checkQuestion);
            }}
            value={checkQuestion}
            disabled={this.props.isLoadingQuestions}
          />
        </div>
        {/* Column 2 - Question */}
        <div
          style={{ width: '16%', cursor: 'pointer' }}
          className={`tRow borderQuestion tRow-no-padding leftJustify${questionCellErrorClass}`}
          onClick={() =>
            this.openEditionModal(
              question,
              question,
              'Question',
              false,
              true,
              multipleOption,
            )
          }
        >
          <div className={aiQuestion ? 'columnDirection ' : null}>
            <div
              style={{ height: '100%' }}
              className={nonModifiedQuestion ? 'questionNonModified' : 'p-1'}
            >
              <div className="questionGridCellContent leftJustify">
                <div className="textContainer">
                  {multipleOption ? (
                    <div
                      className="divContent"
                      style={{ width: '100%' }}
                      title={ConvertHtmlToPlainText(
                        this.getNonEmptyValue(
                          question.text,
                          question.flashQuestionText,
                        ),
                      )}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(
                          this.renderText(
                            this.getNonEmptyValue(
                              question.text,
                              question.flashQuestionText,
                            ),
                          ),
                        ),
                      }}
                    />
                  ) : (
                    <div
                      className="divContent"
                      style={{ width: '100%' }}
                      title={ConvertHtmlToPlainText(
                        this.getNonEmptyValue(
                          question.flashQuestionText,
                          question.text,
                        ),
                      )}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(
                          this.renderText(
                            this.getNonEmptyValue(
                              question.flashQuestionText,
                              question.text,
                            ),
                          ),
                        ),
                      }}
                    />
                  )}
                </div>
                {question.imageUrl?.includes('http') && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="image-question-preview-container"
                  >
                    <ModalImage
                      small={`${question.imageUrl}?t=${Date.now()}`}
                      large={`${question.imageUrl}?t=${Date.now()}`}
                      className="image-question-preview"
                      hideDownload
                      hideZoom
                    />
                  </div>
                )}
              </div>
            </div>
            {aiQuestion && (
              <div style={{ height: '100%' }}>
                <div
                  className={`titleOriginalResponseContainer ${question.substantialChanges && 'titleOriginalResponseSubstantialChanges'}`}
                  style={{ marginLeft: '-4px' }}
                >
                  <span className="title">Original AI Question</span>
                </div>
                <div
                  className={`originalResponseContainerGrid ${question.substantialChanges && 'originalResponseSubstantialChanges'}`}
                  style={{ marginLeft: '-4px' }}
                >
                  {question.originalResponse.ai_question && (
                    <div
                      className="text"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(
                          question.originalResponse.ai_question,
                        ),
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Column 3-6 - Answers */}
        {multipleOption &&
          qAnswers.map((answer, index) =>
            this.renderAnswer(
              question.originalResponse,
              answer,
              '12%',
              question,
              this.getOptionsLabel(index),
              index,
              duplicateAnswers,
            ),
          )}

        {!multipleOption &&
          this.renderAnswer(
            question.originalResponse,
            correctAnswer,
            '48%',
            question,
            'Answer',
            undefined,
            duplicateAnswers,
          )}
        {/* Column 7 - Software page */}
        <div
          style={{ width: '3%', wordBreak: 'break-all', cursor: 'pointer' }}
          className="tRow"
          onClick={() =>
            this.openEditionModal(
              question,
              question,
              'Software page',
              false,
              false,
              false,
              false,
              true,
            )
          }
        >
          {question.softwarePage}
        </div>
        {/* Column 8 - Document page */}
        <div
          style={{ width: '3%', wordBreak: 'break-all', cursor: 'pointer' }}
          className="tRow"
          onClick={() =>
            this.openEditionModal(
              question,
              question,
              'Document page',
              false,
              false,
              false,
              false,
              true,
            )
          }
        >
          {question.documentPage}
        </div>
        {/* Column 9 - Further reading */}
        <div
          style={{ width: '16%', cursor: 'pointer' }}
          className={`tRow${
            hasQuoteCharacter(question.notes) ? ' cellContentError' : ''
          }`}
          onClick={() =>
            this.openEditionModal(
              question,
              question,
              'Further reading',
              false,
              false,
              multipleOption,
            )
          }
        >
          <div style={{ width: '100%' }}>
            <div
              className="further-reading leftJustify"
              style={{ width: '100%' }}
              title={ConvertHtmlToPlainText(question.notes)}
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(
                  this.spellCheck(this.renderWithoutImages(question.notes)),
                ),
              }}
            />
            {furtherReadingImage && (
              <div className="furtherreading">
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="image-question-preview-container"
                >
                  <ModalImage
                    small={`${furtherReadingImage}?t=${Date.now()}`}
                    large={`${furtherReadingImage}?t=${Date.now()}`}
                    className="image-question-preview"
                    hideDownload
                    hideZoom
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Column 10 - Admin Feedback */}
        <div
          style={{ width: '10%', cursor: 'pointer' }}
          className={`tRow${
            hasQuoteCharacter(question.aiAdminFeedback)
              ? ' cellContentError'
              : ''
          }`}
          onClick={() =>
            this.openEditionModal(question, question, 'Admin AI Feedback')
          }
        >
          <div style={{ width: '100%' }}>
            <div
              className="admin-feedback leftJustify"
              style={{ width: '100%' }}
              title={ConvertHtmlToPlainText(question.aiAdminFeedback)}
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(
                  this.spellCheck(
                    this.renderWithoutImages(question.aiAdminFeedback),
                  ),
                ),
              }}
            />
          </div>
        </div>
        {/* Column 11 - Options */}
        <div
          style={{
            width: '1.5%',
            overflow: 'visible',
            display: 'flex',
            justifyContent: 'center',
          }}
          className="tRow"
        >
          <DropDown
            style={{
              top: '-20px',
              position: 'absolute',
              left: '-160px',
              zIndex: 10000,
            }}
            title={question.id}
            options={this.getOptions(question)}
            furtherReadingOption
          />
        </div>

        {this.state.isQuestionIndexModalOpen && (
          <UpdateQuestionIndexModal
            isQuestionIndexModalOpen={this.state.isQuestionIndexModalOpen}
            handleToggleQuestionIndexModal={this.handleToggleQuestionIndexModal}
            question={question}
            handleChangeQuestionIndex={this.handleChangeQuestionIndex}
            amountOfQuestions={this.props.amountOfQuestions}
          />
        )}
      </div>
    );
  }
}
